import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, CalendarDays, List, Plus, Minus, CalendarClock, FileDown, FileSpreadsheet, CalendarRange } from "lucide-react";
import { UserAvatar } from "@/components/TaskShared";
import { TaskDetailDialog } from "@/components/TaskDetailDialog";
import { SavedViews } from "@/components/SavedViews";
import { DateRangePicker } from "@/components/DateRangePicker";
import { startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subMonths, subDays, differenceInCalendarDays } from "date-fns";

const fmtH = (m) => { const h = Math.floor(m / 60); const r = m % 60; return r ? `${h}h ${r}m` : `${h}h`; };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const rangeLabel = (a, b) => {
  const s = new Date(a + "T00:00:00"), e = new Date(b + "T00:00:00");
  const o = { day: "numeric", month: "short" };
  return `${s.toLocaleDateString("en-AU", o)} - ${e.toLocaleDateString("en-AU", { ...o, year: "numeric" })}`;
};

const tsPresets = () => {
  const now = new Date(); const ws = { weekStartsOn: 1 }; const lw = subWeeks(now, 1); const lm = subMonths(now, 1);
  return [
    { key: "last-week", label: "Last week", range: { from: startOfWeek(lw, ws), to: endOfWeek(lw, ws) } },
    { key: "this-month", label: "This month", range: { from: startOfMonth(now), to: endOfMonth(now) } },
    { key: "last-month", label: "Last month", range: { from: startOfMonth(lm), to: endOfMonth(lm) } },
    { key: "last-30", label: "Last 30 days", range: { from: subDays(now, 29), to: now } },
  ];
};

export default function ConsoleTimesheet({ user, canEdit = true, canEditTasks = true, canEstimate = false }) {
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return iso(d); });
  const [view, setView] = useState("list");
  const [range, setRange] = useState(undefined);
  const hasRange = !!(range?.from && range?.to);
  const query = hasRange ? `start=${iso(range.from)}&end=${iso(range.to)}` : `start=${anchor}`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addTask, setAddTask] = useState("");
  const [openTask, setOpenTask] = useState(null);
  const [users, setUsers] = useState([]);
  const me = { id: user?.id, name: user?.name, has_avatar: !!user?.avatar_path };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/console/timesheet?${query}`); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { if (range?.from && !range?.to) return; load(); /* eslint-disable-next-line */ }, [query]);
  const pickRange = (r) => {
    if (r?.from && r?.to && differenceInCalendarDays(r.to, r.from) > 92) { toast.error("Pick a range of 3 months or less"); return; }
    setRange(r);
  };
  useEffect(() => { api.get("/agency/users").then(({ data }) => setUsers(data.filter((u) => u.status !== "invited"))).catch(() => {}); }, []);

  const shift = (days) => { const d = new Date(anchor + "T00:00:00"); d.setDate(d.getDate() + days); setAnchor(iso(d)); };
  const thisWeek = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setRange(undefined); setAnchor(iso(d)); };

  const logTime = async (taskId, delta, date) => {
    if (!canEdit) return;
    try { await api.post(`/console/timesheet/log`, { task_id: taskId, delta, date }); load(); }
    catch (e) {
      if (e?.response?.status === 404) { toast.error("That task no longer exists — refreshing your timesheet."); load(); return; }
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const addToToday = async () => { if (!addTask) return; await logTime(addTask, 15, iso(new Date())); setAddTask(""); };
  const moveTime = async (taskId, fromDate, toDate) => {
    if (!canEdit || fromDate === toDate) return;
    try {
      const { data: res } = await api.post(`/console/tasks/${taskId}/move-time`, { from_date: fromDate, to_date: toDate });
      const m = res.moved_minutes || 0; const h = Math.floor(m / 60); const r = m % 60;
      toast.success(`Moved ${h ? h + "h " : ""}${r}m to ${new Date(toDate + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" })}`);
      load();
    } catch (e) {
      if (e?.response?.status === 404) { toast.error("That task no longer exists — refreshing."); load(); return; }
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const [exporting, setExporting] = useState(false);
  const exportFile = async (ext) => {
    setExporting(ext);
    try {
      const res = await api.get(`/console/timesheet/${ext}?${query}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = data?.is_range ? `timesheet-${data.week.start}-to-${data.week.end}.${ext}` : `timesheet-${data?.week?.start || anchor}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(`Could not export ${ext.toUpperCase()}`); } finally { setExporting(false); }
  };

  const capWeek = data?.is_range ? 0 : (data?.capacity_week_hours || 0);
  const loggedWeekH = useMemo(() => ((data?.week_total_minutes || 0) / 60), [data]);
  const weekPct = capWeek > 0 ? Math.round(loggedWeekH / capWeek * 100) : null;

  return (
    <main className="px-6 md:px-10 py-10 max-w-6xl mx-auto" data-testid="console-timesheet-page">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C]">Timesheet</h1>
          <p className="mt-2 text-sm text-[#9FA1A4]">Your week at a glance. Capacity shown is indicative, not a forecast.</p>
        </div>
        <div className="flex items-center gap-2">
          {["csv", "pdf"].map((ext) => <Button key={ext} data-testid={`timesheet-export-${ext}`} onClick={() => exportFile(ext)} disabled={!!exporting || !data} size="sm" variant="outline" className="border-[#EEEFEF] bg-white text-[#595D62] hover:bg-[#F6F6FD] rounded-full">{exporting === ext ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : ext === "csv" ? <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />} Export {ext.toUpperCase()}</Button>)}
          <div className="flex items-center gap-1 rounded-full border border-[#EEEFEF] bg-white p-1">
            <button data-testid="timesheet-view-list" onClick={() => setView("list")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${view === "list" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}><List className="h-3.5 w-3.5" /> List</button>
            <button data-testid="timesheet-view-calendar" onClick={() => setView("calendar")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${view === "calendar" ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}><CalendarDays className="h-3.5 w-3.5" /> Calendar</button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div className="flex items-center gap-2">
          {!hasRange && <button data-testid="timesheet-prev" onClick={() => shift(-7)} className="h-9 w-9 rounded-full border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><ChevronLeft className="h-4 w-4" /></button>}
          {!hasRange && <button data-testid="timesheet-next" onClick={() => shift(7)} className="h-9 w-9 rounded-full border border-[#EEEFEF] bg-white flex items-center justify-center text-[#595D62] hover:bg-[#F6F6FD]"><ChevronRight className="h-4 w-4" /></button>}
          <Button data-testid="timesheet-today" onClick={thisWeek} size="sm" variant="outline" className="border-[#EEEFEF] bg-white text-[#595D62] rounded-full">This week</Button>
          <DateRangePicker value={range} onChange={pickRange} testid="timesheet-range-picker" presets={tsPresets()} emptyLabel="Date range" />
          <SavedViews screen="timesheet" state={{ view }} defaultState={{ view: "list" }} onApply={(s) => setView(s?.view === "calendar" ? "calendar" : "list")} />
          {data && <span className="text-sm font-medium text-[#06060C] ml-1" data-testid="timesheet-range">{rangeLabel(data.week.start, data.week.end)}</span>}
        </div>
        {data && (
          <div className="flex items-center gap-4">
            {canEdit && data.my_tasks.length > 0 && (
              <div className="flex items-center gap-2">
                <Select value={addTask} onValueChange={setAddTask}><SelectTrigger data-testid="timesheet-add-task" className="w-56 bg-white border-[#EEEFEF] text-xs"><SelectValue placeholder="Log time to a task…" /></SelectTrigger><SelectContent className="bg-white border-[#EEEFEF] max-h-72">{data.my_tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}{t.client_name ? ` · ${t.client_name}` : ""}</SelectItem>)}</SelectContent></Select>
                <Button data-testid="timesheet-add-15" onClick={addToToday} disabled={!addTask} size="sm" className="bg-[#06060C] text-white rounded-full"><Plus className="h-3.5 w-3.5 mr-1" /> 15m today</Button>
              </div>
            )}
            <div className="text-right">
              <p className="font-mono-data text-[9px] uppercase tracking-wider text-[#9FA1A4]" data-testid="timesheet-total-label">{data.is_range ? "Period logged" : "Week logged"}{capWeek ? ` / ${capWeek}h cap` : ""}</p>
              <p className="font-display text-lg font-semibold text-[#06060C]" data-testid="timesheet-week-total">{fmtH(data.week_total_minutes)}{weekPct != null ? ` · ${weekPct}%` : ""}</p>
            </div>
          </div>
        )}
      </div>

      {loading || !data ? <div className="py-24 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div>
        : view === "list" ? <ListView data={data} onLog={logTime} onOpen={setOpenTask} onMove={moveTime} me={me} canLog={canEdit} />
        : <CalendarView data={data} onLog={logTime} onOpen={setOpenTask} onMove={moveTime} me={me} canLog={canEdit} />}

      {openTask && <TaskDetailDialog taskId={openTask} users={users} canEdit={canEditTasks} canEstimate={canEstimate} canMove={canEstimate} onClose={() => setOpenTask(null)} onChanged={load} />}
    </main>
  );
}

function CapBar({ mins, capHours }) {
  if (!capHours) return null;
  const pct = Math.min(100, Math.round((mins / 60) / capHours * 100));
  const over = (mins / 60) > capHours;
  return <div className="h-1.5 rounded-full bg-[#F1F1F5] overflow-hidden mt-2"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: over ? "#F95B5F" : "#30BD79" }} /></div>;
}

function MoveDatePicker({ date, taskId, onMove }) {
  const [open, setOpen] = useState(false);
  const label = new Date(date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`timesheet-move-date-${taskId}`}
          onClick={(e) => e.stopPropagation()}
          title="Change the date this time is allocated to"
          className="inline-flex items-center gap-1 rounded-full border border-[#EEEFEF] bg-white px-2 py-1 text-[11px] font-mono-data text-[#595D62] hover:border-[#d9ccff] hover:text-[#5b2fd6] transition-colors"
        >
          <CalendarRange className="h-3 w-3" /> {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0 bg-white border-[#EEEFEF]" onClick={(e) => e.stopPropagation()} data-testid="timesheet-move-calendar">
        <Calendar
          mode="single"
          selected={new Date(date + "T00:00:00")}
          onSelect={(d) => {
            if (!d) return;
            const to = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            setOpen(false);
            onMove(taskId, date, to);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function EntryRow({ e, date, onLog, onOpen, onMove, me, canLog = true }) {
  return (
    <div data-testid={`timesheet-entry-${e.task_id}`} onClick={() => onOpen(e.task_id)} className="flex items-center gap-3 rounded-xl border border-[#f0f0f3] bg-white px-3 py-2 cursor-pointer hover:border-[#d9ccff] transition-colors">
      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: e.client_accent || "#723FFF" }} />
      {me?.id && <UserAvatar name={me.name} uid={me.id} hasAvatar={me.has_avatar} size={22} />}
      <div className="min-w-0 flex-1"><p className="text-sm text-[#06060C] truncate">{e.title}</p><p className="text-[11px] text-[#9FA1A4] truncate font-mono-data">{e.client_name}{e.project_name ? ` · ${e.project_name}` : ""}</p></div>
      {canLog && date && onMove && <div onClick={(ev) => ev.stopPropagation()} className="shrink-0"><MoveDatePicker date={date} taskId={e.task_id} onMove={onMove} /></div>}
      <div className="flex items-center gap-1 shrink-0" onClick={(ev) => ev.stopPropagation()}>
        {canLog && <button data-testid={`timesheet-minus-${e.task_id}`} onClick={() => onLog(e.task_id, -15, date)} className="h-6 w-6 rounded-full border border-[#EEEFEF] flex items-center justify-center text-[#9FA1A4] hover:text-rose-600"><Minus className="h-3 w-3" /></button>}
        <span className="font-mono-data text-xs text-[#06060C] w-14 text-center">{fmtH(e.minutes)}</span>
        {canLog && <button data-testid={`timesheet-plus-${e.task_id}`} onClick={() => onLog(e.task_id, 15, date)} className="h-6 w-6 rounded-full border border-[#EEEFEF] flex items-center justify-center text-[#9FA1A4] hover:text-[#5b2fd6]"><Plus className="h-3 w-3" /></button>}
      </div>
    </div>
  );
}

function ListView({ data, onLog, onOpen, onMove, me, canLog = true }) {
  return (
    <div className="space-y-4" data-testid="timesheet-list">
      {data.is_range && !data.days.some((d) => d.entries.length) && <p className="text-sm text-[#9FA1A4] py-10 text-center" data-testid="timesheet-range-empty">No time logged in this period.</p>}
      {data.days.filter((d) => !data.is_range || d.entries.length).map((d) => (
        <div key={d.date} data-testid={`timesheet-day-${d.date}`} className={`rounded-2xl border ${d.is_today ? "border-[#d6c9ff]" : "border-[#EEEFEF]"} bg-white premium-shadow p-4`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className={`font-display font-semibold ${d.is_weekend ? "text-[#9FA1A4]" : "text-[#06060C]"}`}>{d.weekday}</span>
              {d.is_today && <span className="font-mono-data text-[9px] uppercase tracking-wider text-[#5b2fd6] bg-[#EDE7FF] rounded-full px-2 py-0.5">Today</span>}
              <span className="text-[11px] text-[#c4c5cc] font-mono-data">{new Date(d.date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</span>
            </div>
            <span className="font-mono-data text-xs text-[#595D62]">{fmtH(d.total_minutes)}{d.capacity_hours ? ` / ${d.capacity_hours}h` : ""}</span>
          </div>
          {d.entries.length === 0 ? <p className="text-xs text-[#c4c5cc]">No time logged.</p> : <div className="space-y-2">{d.entries.map((e) => <EntryRow key={e.task_id} e={e} date={d.date} onLog={onLog} onOpen={onOpen} onMove={onMove} me={me} canLog={canLog} />)}</div>}
          <CapBar mins={d.total_minutes} capHours={d.capacity_hours} />
        </div>
      ))}
    </div>
  );
}

function CalendarView({ data, onLog, onOpen, onMove, me, canLog = true }) {
  return (
    <div className="overflow-x-auto -mx-1 px-1" data-testid="timesheet-calendar">
      <div className="grid grid-cols-7 gap-2 min-w-[900px]">
        {data.days.map((d) => (
          <div key={d.date} data-testid={`timesheet-cal-${d.date}`} className={`rounded-2xl border ${d.is_today ? "border-[#d6c9ff] bg-[#F8F5FF]" : "border-[#EEEFEF] bg-white"} p-2.5 min-h-[220px] flex flex-col`}>
            <div className="text-center mb-2">
              <p className={`font-mono-data text-[10px] uppercase tracking-wider ${d.is_weekend ? "text-[#c4c5cc]" : "text-[#595D62]"}`}>{d.short}</p>
              <p className={`font-display text-lg font-semibold ${d.is_today ? "text-[#5b2fd6]" : "text-[#06060C]"}`}>{new Date(d.date + "T00:00:00").getDate()}</p>
            </div>
            <div className="space-y-1.5 flex-1">
              {d.entries.map((e) => (
                <div key={e.task_id} onClick={() => onOpen(e.task_id)} className="border border-[#EEEFEF] bg-white p-1.5 cursor-pointer hover:border-[#d9ccff] transition-colors" style={{ borderRadius: "3px" }}>
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: e.client_accent || "#723FFF" }} />
                    {me?.id && <UserAvatar name={me.name} uid={me.id} hasAvatar={me.has_avatar} size={16} />}
                    <p className="text-[11px] text-[#06060C] leading-tight line-clamp-2 flex-1">{e.title}</p>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="font-mono-data text-[10px] text-[#5b2fd6]">{fmtH(e.minutes)}</span>
                    <div className="flex items-center gap-0.5" onClick={(ev) => ev.stopPropagation()}>
                      {canLog && <button onClick={() => onLog(e.task_id, -15, d.date)} className="h-4 w-4 rounded border border-[#EEEFEF] flex items-center justify-center text-[#9FA1A4] hover:text-rose-600"><Minus className="h-2.5 w-2.5" /></button>}
                      {canLog && <button onClick={() => onLog(e.task_id, 15, d.date)} className="h-4 w-4 rounded border border-[#EEEFEF] flex items-center justify-center text-[#9FA1A4] hover:text-[#5b2fd6]"><Plus className="h-2.5 w-2.5" /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-[#f0f0f3] flex items-center justify-between">
              <span className="flex items-center gap-1 text-[10px] text-[#9FA1A4] font-mono-data"><CalendarClock className="h-3 w-3" />{fmtH(d.total_minutes)}</span>
              {d.capacity_hours ? <span className="text-[10px] text-[#c4c5cc] font-mono-data">/ {d.capacity_hours}h</span> : null}
            </div>
            <CapBar mins={d.total_minutes} capHours={d.capacity_hours} />
          </div>
        ))}
      </div>
    </div>
  );
}
